import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

interface OpenHelpArgs {
  guideId?: string;
  query?: string;
}

interface HelpPanelContextValue {
  isOpen: boolean;
  activeGuideId: string | null;
  initialQuery: string;
  openHelp: (args?: OpenHelpArgs) => void;
  closeHelp: () => void;
  setActiveGuideId: (id: string | null) => void;
}

const HelpPanelContext = createContext<HelpPanelContextValue | undefined>(undefined);

export function HelpPanelProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeGuideId, setActiveGuideId] = useState<string | null>(null);
  const [initialQuery, setInitialQuery] = useState('');

  const openHelp = useCallback((args?: OpenHelpArgs) => {
    setActiveGuideId(args?.guideId ?? null);
    setInitialQuery(args?.query ?? '');
    setIsOpen(true);
  }, []);

  const closeHelp = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Global "?" shortcut — opens the panel from anywhere.
  // Suppressed while typing in inputs/textareas/contentEditable.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== '?') return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      // Avoid colliding with modifier-key chords
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      e.preventDefault();
      setIsOpen((prev) => !prev);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <HelpPanelContext.Provider
      value={{ isOpen, activeGuideId, initialQuery, openHelp, closeHelp, setActiveGuideId }}
    >
      {children}
    </HelpPanelContext.Provider>
  );
}

export function useHelpPanel() {
  const ctx = useContext(HelpPanelContext);
  if (!ctx) throw new Error('useHelpPanel must be used within a HelpPanelProvider');
  return ctx;
}
